import type { FunctionComponent } from 'react';

import type { TestId } from '@trello/test-ids';

import type { IconSize } from '../src/components/Icon';
import { Icon } from '../src/components/Icon';

export interface GlyphProps {
  /**
   * A string that is applied as an aria attribute on the icon.
   */
  label?: string;
  /**
   * The color that the Icon should be rendered as.
   * @default token('color.icon', '#42526E')
   */
  color?: string;
  /**
   * The size to render the Icon size.
   * @default "medium"
   */
  size?: IconSize;
  /**
   * A string that gets placed as a data attribute (data-testid) onto the
   * Icon wrapper so that our
   * smoketest can properly target and test the component
   * @default undefined
   */
  testId?: TestId | string;
  // Escape hatches
  /**
   * ⚠️ DO NOT USE THIS PROP UNLESS YOU REALLY REALLY HAVE TO.
   *
   * Places a class name on the Icon (more specifically, the svg itself). This
   * is placed in addition to the classes already placed on the Icon. This is
   * placed directly onto the SVG via the glyph templates that are
   * generated by IconGlyph.template.js
   *
   * Please refrain from using this unless absolutely necessary.
   * @default undefined
   */
  dangerous_className?: string;
  /**
   * The switch for the icon to be centered in the dedicated space with padding around it.
   * Designed for cases when icon is not inline.
   */
  block?: boolean;
}

const CollapseToggleIconGlyph = () => {
  return (
    <svg
      width="16"
      height="6"
      viewBox="0 0 16 6"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2 0C1.17157 0 0.5 0.67155 0.5 1.5V4.5C0.5 5.32845 1.17157 6 2 6H14C14.8285 6 15.5 5.32845 15.5 4.5V1.5C15.5 0.67155 14.8285 0 14 0H2ZM2 2.25C2 1.83578 2.33579 1.5 2.75 1.5H4.25C4.66423 1.5 5 1.83578 5 2.25V3.75C5 4.16423 4.66423 4.5 4.25 4.5H2.75C2.33579 4.5 2 4.16423 2 3.75V2.25ZM5.75 3C5.75 2.79292 5.91792 2.625 6.125 2.625H13.625C13.8321 2.625 14 2.79292 14 3C14 3.20708 13.8321 3.375 13.625 3.375H6.125C5.91792 3.375 5.75 3.20708 5.75 3Z"
        fill="currentColor"
      />
    </svg>
  );
};

export const CollapseToggleIcon: FunctionComponent<GlyphProps> = (props) => {
  const { testId, dangerous_className, size, color, label, block } = props;
  return (
    <Icon
      testId={testId || 'CollapseToggleIcon'}
      size={size}
      dangerous_className={dangerous_className}
      color={color}
      block={block}
      label={label}
      glyph={CollapseToggleIconGlyph}
    />
  );
};
